import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A panel anchored to a control, portalled out of whatever is holding it.
 *
 * The portal is the whole reason this exists rather than an in-place expansion like
 * `features/endpoint-picker.tsx` does. That picker lives inside `DialogBody`, which scrolls, so
 * anything absolutely positioned inside it is clipped at the fold — expanding in place is the
 * right answer there. Its first caller here is the softphone dock, which is 320 pixels wide,
 * `position: fixed`, and clips its own overflow: a list that pushed the Call button down would
 * push it off the bottom of the dock.
 *
 * `collisionPadding` rather than the default 0 so a panel opened from the dock — which sits in
 * the corner — is nudged away from the viewport edge instead of touching it.
 */
export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export function PopoverContent({
  className, align = "start", sideOffset = 6, collisionPadding = 8, ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          "z-50 rounded-lg border border-border bg-surface-raised p-1 shadow-lg",
          "max-w-[var(--radix-popover-content-available-width)]",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}
